import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { CTAButton } from "./CTAButton";
import { LanguageSwitcher } from "./LanguageSwitcher";

const NAV = [
  { to: "/plans", key: "plans" },
  { to: "/smartbag", key: "smartbag" },
  { to: "/telemedicine", key: "telemedicine" },
  { to: "/connectivity", key: "connectivity" },
  { to: "/risk-options", key: "riskOptions" },
  { to: "/traders-conferences", key: "traders" },
  { to: "/partners", key: "partners" },
];

export function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="header-logo-link" aria-label="R0AM home">
          <Logo size="sm" testId="header-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={`text-sm font-medium transition-colors hover:text-white ${
                location.pathname === item.to ? "text-white" : "text-white/65"
              }`}
            >
              {t(`nav.${item.key}`)}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <CTAButton to="/quote" variant="primary" size="sm" testId="header-quote-cta">
            {t("common.getQuote")}
          </CTAButton>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <LanguageSwitcher variant="mobile" />
          <button
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-header border-t border-white/10" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-1">
            <Link to="/" className="py-3 text-white/80 hover:text-white" data-testid="mnav-home">{t("common.home")}</Link>
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="py-3 text-white/80 hover:text-white border-t border-white/5"
                data-testid={`mnav-${item.to.replace("/", "")}`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            <Link to="/compliance" className="py-3 text-white/60 text-sm border-t border-white/5" data-testid="mnav-compliance">{t("nav.compliance")}</Link>
            <Link to="/faq" className="py-3 text-white/60 text-sm" data-testid="mnav-faq">{t("nav.faq")}</Link>
            <div className="mt-3">
              <CTAButton to="/quote" variant="primary" fullWidth testId="mobile-quote-cta">
                {t("common.getQuote")}
              </CTAButton>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
